/*
 * Gridstack-based implementation of the LayoutHost seam. This is the only file
 * that imports gridstack FOR THE LAYOUTHOST SEAM — the rest of the dashboard
 * talks to the `LayoutHost` interface, so swapping to a different engine later
 * is a single-file change for the dashboard.
 *
 * Seit 2026-09-16 gibt es einen zweiten, bewusst anders gelagerten Nutzer:
 * `hmi/editor/hmiGridHost.ts`. Er benutzt dieselbe Bibliothek, aber NICHT
 * diesen Seam, und der Grund ist der Unterschied genau eine Zeile weiter
 * unten: der HMI-Editor darf `engineState` nicht speichern. Ein HmiProject
 * rendert auch die Kotlin-Panel-App, und die kennt kein Gridstack; eine
 * Anordnung, die nur im Engine-Format existiert, waere dort leer. Dort ist
 * Gridstack deshalb reine Interaktion, und gespeichert wird ausschliesslich
 * das portable Schema (col/row/colSpan/rowSpan je Layout-Ziel). Den Seam
 * dafuer zu erweitern haette ihn fuer den Dashboard-Fall verkompliziert, um
 * einen Fall abzudecken, der sein Engine-Format gerade nicht will.
 *
 * The engine-native state ("column count, x/y/w/h per cell") is stored in
 * `LayoutBody.engineState`. The portable widget list travels alongside in
 * `LayoutBody.widgets`. Widgets are keyed by their `id`; the corresponding
 * grid item carries the same id via Gridstack's `gs-id` attribute so we can
 * pair them up when serializing.
 */

import { GridStack, type GridStackOptions, type GridStackWidget } from 'gridstack';
import { effect, type Signal } from '@preact/signals';
import type { LayoutBody, LayoutHost, LayoutMode, WidgetInstance } from './layoutHost';

/**
 * Engine state we persist. Gridstack's own `save()` returns a
 * `GridStackWidget[]`; we keep exactly that (minus content) so a round-trip
 * is lossless.
 */
interface GridEngineState {
  items: GridStackWidget[];
}

/**
 * Layout host backed by Gridstack. Keeps its DOM inside the caller's
 * container and reports every mutation via `onChange`.
 */
export class GridLayoutHost implements LayoutHost {
  private static readonly DefaultOptions: GridStackOptions = {
    column: 12,
    cellHeight: 80,
    margin: 8,
    // Only take the horizontal float — vertical is Gridstack's default anyway.
    float: false,
  };

  mount(
    container: HTMLElement,
    initial: LayoutBody | undefined,
    onChange: (body: LayoutBody) => void,
    mode: Signal<LayoutMode>,
  ): () => void {
    // Gridstack needs an element with the `.grid-stack` class. Rather than
    // require the caller to lay it out, we create one and clean it up on
    // unmount — the container remains theirs.
    const gridEl = document.createElement('div');
    gridEl.className = 'grid-stack';
    container.appendChild(gridEl);

    const grid = GridStack.init({ ...GridLayoutHost.DefaultOptions }, gridEl);

    // Seed items from the persisted engineState. `load` will diff against
    // whatever is currently in the grid (empty at this point) and add rows.
    // `addRemove: true` allows both directions in future re-hydrations.
    const engineState = (initial?.engineState as GridEngineState | undefined) ?? { items: [] };
    if (engineState.items.length > 0) {
      grid.load(engineState.items, true);
    }

    // Every widget instance in the portable list is expected to have a
    // corresponding grid cell — the DashboardPage keeps them in lock-step.
    let currentWidgets: WidgetInstance[] = initial?.widgets ? [...initial.widgets] : [];

    // Serialize + emit on any grid mutation. Save without content — bodies
    // are rendered by Preact into the same DOM node, we don't want to persist
    // the framework's DOM string.
    const emit = () => {
      const items = grid.save(false, false) as GridStackWidget[];
      const body: LayoutBody = {
        widgets: currentWidgets,
        engineState: { items } satisfies GridEngineState,
      };
      onChange(body);
    };

    grid.on('change added removed', emit);

    // React to mode changes. Signals + effect give us auto-subscription;
    // when the DashboardPage flips the toggle we lock/unlock the grid.
    const disposeMode = effect(() => {
      const editable = mode.value === 'edit';
      grid.enableMove(editable, true);
      grid.enableResize(editable, true);
    });

    // Public update port — DashboardPage calls this when it adds/removes a
    // widget instance so the host can keep its portable list in sync without
    // rebuilding the whole grid.
    (gridEl as GridEl).__setWidgets = (next: WidgetInstance[]) => {
      currentWidgets = [...next];
    };

    // Adds one widget to the grid at runtime, keeping the portable list and
    // Gridstack's engine state in lock-step. Position defaults are chosen to
    // let Gridstack auto-flow a new cell into the first free row when the
    // caller doesn't care where.
    (gridEl as GridEl).__addWidget = (instance: WidgetInstance, opts: AddWidgetOptions = {}) => {
      currentWidgets.push(instance);
      const gsItem: GridStackWidget = {
        id: instance.id,
        w: opts.w ?? 4,
        h: opts.h ?? 3,
        autoPosition: opts.x === undefined || opts.y === undefined,
      };
      if (opts.x !== undefined) gsItem.x = opts.x;
      if (opts.y !== undefined) gsItem.y = opts.y;
      grid.addWidget(gsItem);
      emit();
    };

    // Removes a widget from the grid + portable list, keeping the two in sync.
    (gridEl as GridEl).__removeWidget = (id: string) => {
      currentWidgets = currentWidgets.filter((w) => w.id !== id);
      const el = gridEl.querySelector<HTMLElement>(`.grid-stack-item[gs-id="${CSS.escape(id)}"]`);
      if (el) grid.removeWidget(el, true);
      emit();
    };

    return () => {
      disposeMode();
      grid.off('change added removed');
      // `destroy(true)` removes the grid DOM too — but we own gridEl, remove
      // it explicitly so `container` returns to its original shape.
      grid.destroy(false);
      if (gridEl.parentNode === container) {
        container.removeChild(gridEl);
      }
    };
  }
}

/** Optional placement hints when adding a widget at runtime. Omitting x/y
 *  makes Gridstack auto-position the cell in the first free spot. */
export interface AddWidgetOptions {
  x?: number;
  y?: number;
  w?: number;
  h?: number;
}

/**
 * Hidden port on the grid element so DashboardPage can push widget-list
 * mutations into the host without going through the LayoutHost interface
 * (which is intentionally read-only — mutations flow via `onChange`).
 */
interface GridEl extends HTMLElement {
  __setWidgets?: (next: WidgetInstance[]) => void;
  __addWidget?: (instance: WidgetInstance, opts?: AddWidgetOptions) => void;
  __removeWidget?: (id: string) => void;
}

/**
 * Utility: after mount, callers can look the container up and push new
 * widget lists (used when the page "adds a widget" but the grid engine
 * needs a companion `addWidget` call — kept out of LayoutHost so the
 * abstraction stays engine-agnostic).
 */
export function pushWidgets(container: HTMLElement, widgets: WidgetInstance[]): void {
  const gridEl = container.querySelector('.grid-stack') as GridEl | null;
  gridEl?.__setWidgets?.(widgets);
}

/** Add a widget to the mounted host. Idempotent-safe — repeated calls with
 *  the same instance id are harmless because Gridstack replaces on id
 *  collision. */
export function addWidgetToHost(
  container: HTMLElement,
  instance: WidgetInstance,
  opts?: AddWidgetOptions,
): void {
  const gridEl = container.querySelector('.grid-stack') as GridEl | null;
  gridEl?.__addWidget?.(instance, opts);
}

/** Remove a widget from the mounted host by id. */
export function removeWidgetFromHost(container: HTMLElement, id: string): void {
  const gridEl = container.querySelector('.grid-stack') as GridEl | null;
  gridEl?.__removeWidget?.(id);
}
